import { buildChainedModelId, brainLegEntries, catalogRenameAliases, brainWalletForGroup, CLAUDE_OAUTH_GROUP, isBrainCatalogEntry, isBrainModelSelected, isClaudeFamilyModel, isImageGenCatalogEntry, isPersonalPiWallet, parseModelChain, piBrainLineBases, protocolAvailability, stripModelSuffixTag, unavailableReasonI18nKey, } from "./model-registry.js";
const BRAIN_SKIP_REASON_CODES = new Set([
    "not_in_catalog",
    "no_brain_leg",
    "not_selected",
    "not_pi_selected",
    "no_group",
    "wrong_chain",
]);
/** Resolve either catalog availability or reconcile skip reasons to a locale key. */
export function brainSkipReasonI18nKey(reason) {
    const unavailableKey = unavailableReasonI18nKey(reason);
    if (unavailableKey)
        return unavailableKey;
    return reason && BRAIN_SKIP_REASON_CODES.has(reason)
        ? "brain.skipReason." + reason
        : undefined;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function normalizeModelId(raw) {
    if (typeof raw !== "string")
        return undefined;
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    // Strip only the chain marker and ordinary suffix tags before rebuilding the canonical variant.
    // This preserves [pi] while removing [1m] and prevents [pi][pi] on repeated writes.
    const { chain, withoutChain } = parseModelChain(trimmed);
    const base = stripModelSuffixTag(withoutChain).trim();
    if (!base)
        return undefined;
    return buildChainedModelId(base, chain);
}
function normalizePriorityItem(raw) {
    const modelId = typeof raw === "string"
        ? normalizeModelId(raw)
        : isRecord(raw)
            ? normalizeModelId(raw.modelId)
            : undefined;
    return modelId ? { modelId } : undefined;
}
function normalizePriorityEntries(raw) {
    if (!Array.isArray(raw))
        return [];
    const entries = [];
    const seen = new Set();
    for (const item of raw) {
        const entry = normalizePriorityItem(item);
        if (!entry || seen.has(entry.modelId))
            continue;
        seen.add(entry.modelId);
        entries.push(entry);
    }
    return entries;
}
/**
 * 唯一主脑优先级写入 builder：接受 string[] 或 {modelId}[]，归一为 base + 可选 [pi] 变体，
 * 去重保序；空列表代表未配置，因此返回 undefined。
 */
export function buildBrainPriorityList(raw, actor, now) {
    const entries = normalizePriorityEntries(raw);
    if (entries.length === 0)
        return undefined;
    return {
        entries,
        editedAt: (now ?? new Date()).toISOString(),
        editedBy: actor,
    };
}
/** 读库/读 wire 的边界 guard：整体畸形返回 undefined，畸形条目只跳过，元数据缺失降为空串。 */
export function parseBrainPriorityList(raw) {
    if (!isRecord(raw) || !Array.isArray(raw.entries))
        return undefined;
    return {
        entries: normalizePriorityEntries(raw.entries),
        editedAt: typeof raw.editedAt === "string" ? raw.editedAt : "",
        editedBy: typeof raw.editedBy === "string" ? raw.editedBy : "",
    };
}
export function isBrainSelectionSource(value) {
    return value === "employee" || value === "platform-default";
}
function isBrainSelectionFailure(value) {
    return value === "all_unavailable" || value === "empty_list" || value === "no_catalog";
}
function normalizeBrainPick(raw) {
    if (!isRecord(raw) || typeof raw.group !== "string")
        return undefined;
    const modelId = normalizeModelId(raw.modelId);
    const group = raw.group.trim();
    if (!modelId || !group)
        return undefined;
    return { modelId, group };
}
function normalizeSkippedEntry(raw) {
    if (!isRecord(raw))
        return undefined;
    const modelId = normalizeModelId(raw.modelId);
    if (!modelId)
        return undefined;
    if (typeof raw.unavailableReason !== "string")
        return { modelId };
    return { modelId, unavailableReason: raw.unavailableReason };
}
/**
 * 「这个主脑是员工自己的」来源标记——员工手选（`buildMainModelSettings`）、会员卡片「设为主脑」、
 * 个人订阅上新自动换主脑共用这一个形状。个人订阅主脑归员工，平台优先级对账不接管
 * （`reconcileDefaultBrain` 员工分支）；漏写这个标记，企业员工的个人订阅主脑会在下一次对账时被
 * 平台默认换掉。
 */
export function buildEmployeeBrainSelection(now) {
    return { source: "employee", skipped: [], at: (now ?? new Date()).toISOString() };
}
/** 解析已物化的选择结果；旧 source 直接拒绝，未知 unavailableReason 原样保留。 */
export function parseBrainSelection(raw) {
    if (!isRecord(raw) || !isBrainSelectionSource(raw.source))
        return undefined;
    const skipped = Array.isArray(raw.skipped)
        ? raw.skipped.flatMap((item) => {
            const normalized = normalizeSkippedEntry(item);
            return normalized ? [normalized] : [];
        })
        : [];
    const selection = {
        source: raw.source,
        skipped,
        at: typeof raw.at === "string" ? raw.at : "",
    };
    const chosen = normalizeBrainPick(raw.chosen);
    if (chosen)
        selection.chosen = chosen;
    if (isBrainSelectionFailure(raw.failure))
        selection.failure = raw.failure;
    if (typeof raw.listEditedAt === "string")
        selection.listEditedAt = raw.listEditedAt;
    return selection;
}
export function aggregateBrainRows(rows) {
    let sawUnavailable = false;
    let reason;
    // aggregateModelAvailability joins raw modelId exactly. The caller matches after stripping suffixes,
    // so evaluate every matched row directly while retaining protocolAvailability as the single source
    // of truth; this keeps [1m] variants from hiding another protocol row for the same base model.
    for (const row of rows) {
        const availability = protocolAvailability(row);
        if (availability.level === "available")
            return { available: true };
        if (availability.level === "unknown")
            continue;
        sawUnavailable = true;
        if (reason === undefined && row.unavailableReason)
            reason = row.unavailableReason;
    }
    if (!sawUnavailable)
        return { available: true };
    return reason === undefined ? { available: false } : { available: false, unavailableReason: reason };
}
/**
 * 「这个主脑模型的**主脑腿**此刻可不可用」——本仓关于主脑可用性的**唯一判定入口**（红线 #9）。
 *
 * 三个消费面必须共用它，否则同一个模型会在不同面上得出不同结论：
 *  · `reconcileDefaultBrain`（要不要把员工从停机模型上接管走）
 *  · picker / 设置页清单（这一行藏不藏，见 `brain-model-items.ts` 的 availability 装配）
 *  · `resolveDefaultBrain`（清单里这一条跳不跳过——它内部走同一条链）
 *
 * **为什么不能用 `aggregateModelAvailability`**：那个按精确 modelId 聚合**全部协议行**、不过
 * `brainLegEntries` 那层筛。主脑只走得通主脑腿（claude 链 = anthropic_messages，pi 链 = pi 入口行），
 * 于是两者会分歧成两种真实故障：① 主脑腿停了、另有非主脑协议行还活着 → 清单显示可选、
 * reconcile 却接管 → 员工被反复从自己选的模型上踢开；② 主脑腿全 unknown、非主脑行标了 false
 * → 清单把行藏掉、reconcile 不接管 → 员工的选中项凭空消失且无替补。
 *
 * **只认显式 `available === false`**。`undefined` = 网关未部署可用性契约 = unknown，
 * `aggregateBrainRows` 对全 unknown 返回 `available:true`。这是刻意的安全档：旧网关或字段
 * 缺失时绝不能把全员的自选一次性推翻。
 *
 * **模型不在 catalog / 没有主脑协议行 → 空 summary（按 unknown 处置）**。那是「供给缺失」，
 * 与「上游停机」是两件事——catalog 冷启动尚未同步时整张表是空的，按不可用处理会在每次冷启
 * 把员工的选择推翻一遍。供给缺失由圈定判据与 `resolveDefaultBrain` 的 `not_in_catalog` /
 * `no_brain_leg` 两个原因码各自处置。
 *
 * **别把它和 `brainLegAvailabilitySummary`（model-registry.ts）合并**——两者看着同构，
 * 平局判法不同：那个在 degraded + unknown 混合时判 `unknown / blocked:false`（管理后台展示
 * 用，宁可不阻断超管勾选），本函数底层的 `aggregateBrainRows` 在同一输入下判 `available:false`。
 * 本函数的硬约束是**与 `resolveDefaultBrain` 逐格一致**（清单跳过它 ⇔ 员工被接管），换成那个
 * 平局判法会当场破坏这条同进同出。要合并得先统一平局语义，那是另一件事。
 */
export function brainLegAvailability(catalog, modelId) {
    if (!modelId)
        return {};
    const { chain, withoutChain } = parseModelChain(modelId);
    const base = stripModelSuffixTag(withoutChain).trim();
    if (!base)
        return {};
    const rows = brainLegEntries(catalog.filter((entry) => stripModelSuffixTag(entry.modelId) === base), chain);
    // 一行主脑腿都没有 = 供给缺失（不在 catalog / 只有非主脑协议行），不是「上游停机」。
    // 返回空 summary 让消费端按 unknown 处置：picker 不藏、reconcile 不接管。
    if (rows.length === 0)
        return {};
    return aggregateBrainRows(rows);
}
export function isRetiredFromLiveCatalog(liveCatalog, modelId, scope = {}) {
    if (liveCatalog.length === 0 || !modelId)
        return false;
    const { withoutChain } = parseModelChain(modelId);
    const base = stripModelSuffixTag(withoutChain).trim();
    if (!base || base === "inherit")
        return false;
    if (isClaudeFamilyModel(base) && (!base.startsWith("claude-") || scope.wallet !== "gateway"))
        return false;
    if (scope.otherWalletBases?.has(base))
        return false;
    // 改名别名与凭证解析同源：旧 id 仍能解析到改名后的行 = 还能用，不是下线。
    const candidates = new Set([base, ...catalogRenameAliases(base)]);
    return !liveCatalog.some((entry) => candidates.has(stripModelSuffixTag(entry.modelId).trim()));
}
/** {@link brainLegAvailability} 的布尔便利包装——只认显式不可用。 */
export function isBrainPickUpstreamUnavailable(catalog, modelId) {
    return brainLegAvailability(catalog, modelId).available === false;
}
function normalizePiSelection(piSelection) {
    const selected = new Set();
    for (const raw of piSelection ?? []) {
        const normalized = normalizeModelId(raw);
        if (!normalized)
            continue;
        const { withoutChain } = parseModelChain(normalized);
        const base = stripModelSuffixTag(withoutChain).trim();
        if (base)
            selected.add(base);
    }
    return selected;
}
function firstAvailableRow(rows) {
    return rows.find((row) => {
        const level = protocolAvailability(row).level;
        return level !== "degraded" && level !== "unsupported";
    }) ?? rows[0];
}
export function buildBrainEvalCtx(args) {
    return {
        catalog: args.catalog,
        roleSelection: args.roleSelection,
        orgSelection: args.orgSelection,
        piSelectedBases: normalizePiSelection(args.piSelection),
        brainLineBases: piBrainLineBases(args.catalog, args.piSelection),
        ...(args.isAllowed ? { isAllowed: args.isAllowed } : {}),
    };
}
/**
 * 单次成文形态的逐条判定（N-167 统一选模型函数用）：清单行 → 文本腿 → 此刻可用，返回 base id。
 *
 * 与 {@link evaluateBrainPriorityEntry} 的**唯一差别**是协议行的认法：单次成文经 Claude CLI 发出，
 * 网关翻译的 anthropic 行也接得住，所以认 {@link isBrainCatalogEntry}（anthropic 行、非工具组、非生图，
 * 翻译行也算），不要求 `translated === false`。其余同口径：只认显式 `available === false`
 * （{@link aggregateBrainRows}）；整模型有一行生图就整个剔（与 `deriveAssignableModelIds` 同一判据）；
 * `[pi]` 条目按去掉后缀的 base 判。允许范围不在这里判，由调用方先过。
 */
export function evaluateTextLegEntry(modelId, catalog) {
    const { withoutChain } = parseModelChain(modelId);
    const base = stripModelSuffixTag(withoutChain).trim();
    const anyRows = catalog.filter((entry) => stripModelSuffixTag(entry.modelId) === base);
    if (anyRows.length === 0) {
        return { ok: false, skipped: { modelId, unavailableReason: "not_in_catalog" } };
    }
    const rows = anyRows.some(isImageGenCatalogEntry)
        ? []
        : anyRows.filter((entry) => isBrainCatalogEntry(entry) && entry.supported !== false);
    if (rows.length === 0) {
        return { ok: false, skipped: { modelId, unavailableReason: "no_text_leg" } };
    }
    const availability = aggregateBrainRows(rows);
    if (!availability.available) {
        return {
            ok: false,
            skipped: availability.unavailableReason === undefined
                ? { modelId }
                : { modelId, unavailableReason: availability.unavailableReason },
        };
    }
    return { ok: true, modelId: base };
}
/**
 * 平台优先级一条的判定（pi 白名单 → catalog/brain-leg → 允许范围与 org/role 圈定 → 可用性 → group）。
 * `resolveDefaultBrain`、`resolveBrainCandidateChain` 与统一选模型函数的完整 AI 形态共用，禁止各写一份。
 */
export function evaluateBrainPriorityEntry(entry, ctx) {
    const { chain, withoutChain } = parseModelChain(entry.modelId);
    const base = stripModelSuffixTag(withoutChain).trim();
    if (chain === "pi" && !ctx.piSelectedBases.has(base)) {
        return { ok: false, skipped: { modelId: entry.modelId, unavailableReason: "not_pi_selected" } };
    }
    // 两个原因码处置方式不同：not_in_catalog = 这个模型一行都没供给（去网关加模型/查组授权）；
    // no_brain_leg = 供给了（甚至可用），但没有能当主脑的协议行——claude 链缺 anthropic 主脑行 /
    // pi 链缺 pi 入口行（去网关补协议行，或把它加进 pi 策展）。混成一个码会把排查方向从一开始带偏。
    const anyRows = ctx.catalog.filter((catalogEntry) => stripModelSuffixTag(catalogEntry.modelId) === base);
    const rows = brainLegEntries(anyRows, chain);
    if (rows.length === 0) {
        return {
            ok: false,
            skipped: {
                modelId: entry.modelId,
                unavailableReason: anyRows.length === 0 ? "not_in_catalog" : "no_brain_leg",
            },
        };
    }
    if ((ctx.isAllowed && !ctx.isAllowed(base, chain))
        || !isBrainModelSelected(base, anyRows, ctx.orgSelection, { brainLineBases: ctx.brainLineBases })
        || !isBrainModelSelected(base, anyRows, ctx.roleSelection, { brainLineBases: ctx.brainLineBases })) {
        return { ok: false, skipped: { modelId: entry.modelId, unavailableReason: "not_selected" } };
    }
    const availability = aggregateBrainRows(rows);
    if (!availability.available) {
        return {
            ok: false,
            skipped: availability.unavailableReason === undefined
                ? { modelId: entry.modelId }
                : { modelId: entry.modelId, unavailableReason: availability.unavailableReason },
        };
    }
    const row = firstAvailableRow(rows);
    const group = row?.chosenGroup?.name;
    if (!group) {
        return { ok: false, skipped: { modelId: entry.modelId, unavailableReason: "no_group" } };
    }
    return { ok: true, chosen: { modelId: entry.modelId, group } };
}
/**
 * 按指定协议腿过滤平台默认主脑优先级：剔除另一条腿、无本腿协议行、不在 org/role 圈定的条目，
 * 保平台顺序。云端任务清单与 agent-host 链共用这一份。
 */
export function resolveBrainCandidateChain(args) {
    if (!args.priority || args.priority.entries.length === 0) {
        return { candidates: [], skipped: [] };
    }
    const ctx = buildBrainEvalCtx(args);
    const candidates = [];
    const skipped = [];
    for (const entry of args.priority.entries) {
        if (parseModelChain(entry.modelId).chain !== args.chain) {
            skipped.push({ modelId: entry.modelId, unavailableReason: "wrong_chain" });
            continue;
        }
        const result = evaluateBrainPriorityEntry(entry, ctx);
        if (result.ok) {
            const { withoutChain } = parseModelChain(entry.modelId);
            const base = stripModelSuffixTag(withoutChain).trim();
            candidates.push({ model: buildChainedModelId(base, args.chain) });
            continue;
        }
        skipped.push(result.skipped);
    }
    return { candidates, skipped };
}
/**
 * 「按平台列表跨两条引擎取判过的条目」的唯一实现（N-182 P1 R2.1）：按平台原序逐条交
 * {@link evaluateBrainPriorityEntry} 判（裸 id 按 Claude 腿、`[pi]` 变体按 pi 腿，混排、不先走完一条腿），
 * 返回全部判过的条目与全部跳过的条目，各自保平台序。
 *
 * 桌面默认主脑 {@link resolveDefaultBrain} 取第一个；飞书「跟随主脑」候选链（`resolveFeishuTurnModelChain`）
 * 取全部——两处同输入时首个必然相同，不能各写一份遍历。没有平台列表 → 两个空数组；空列表 / 空清单的
 * 失败分类由调用方判。
 */
export function walkBrainPriority(args) {
    const passed = [];
    const skipped = [];
    if (!args.priority)
        return { passed, skipped };
    const ctx = buildBrainEvalCtx(args);
    for (const entry of args.priority.entries) {
        const result = evaluateBrainPriorityEntry(entry, ctx);
        if (result.ok)
            passed.push({ ...result.chosen, chain: parseModelChain(entry.modelId).chain });
        else
            skipped.push(result.skipped);
    }
    return { passed, skipped };
}
export function resolveDefaultBrain(args) {
    if (!args.priority || args.priority.entries.length === 0) {
        return { ok: false, reason: "empty_list", skipped: [] };
    }
    if (args.catalog.length === 0) {
        return { ok: false, reason: "no_catalog", skipped: [] };
    }
    // 取平台列表遍历的首个判过者（claude 裸 id 与 [pi] 变体混排，不先走完一条腿）。
    const { passed, skipped } = walkBrainPriority(args);
    const first = passed[0];
    if (!first)
        return { ok: false, reason: "all_unavailable", skipped };
    // 跳过摘要只到选中者为止（桌面「已跳过」只讲顺延过了哪些）：它前面的条目全是跳过的，条数 = 它在列表里的位置。
    const position = args.priority.entries.findIndex((entry) => entry.modelId === first.modelId);
    return { ok: true, chosen: { modelId: first.modelId, group: first.group }, skipped: skipped.slice(0, position) };
}
/**
 * 「这个模型 id 压根不属于这张卡」——写入期保险栏的纯谓词（不匹配时返回 reason，匹配返回 undefined）。
 *
 * 只覆盖**已实证的失败类**：个人订阅钱包收到另一家的模型 id。2026-09-06 真机取证里
 * `brainGroupParams["claude-oauth"].defaultModel` 被写成 `gpt-5.6-terra[pi]`，之后
 * `resolveBrainModelName` 会拿这个 id 当 claude-oauth 组的主模型 —— 拿 Claude 订阅的钱包去点
 * 一个 GPT 模型，按不变量 #11 必须明确失败，而不是静默写进去等用户发现发不出消息。
 *
 * 判据只用**家族归属**，不用链路后缀。**别改回按 `[pi]` 后缀判**（2026-09-06 跨家族终审抓到的
 * 假阳）：ChatGPT / xAI 个人订阅的模型行按产品设计就是**裸 id**（`gpt-5.6-luna` / `grok-4.6`，
 * 理由见 `packages/ui/src/lib/brain-model-items.ts` 的 `buildXaiOauthItems` docstring —— 后缀是
 * 消歧手段，这两家只有 pi 一条链路、没有孪生行，挂后缀等于把内部实现名摆给员工），
 * `chatgpt-model-items.test.ts` / `xai-model-items.test.ts` 各自钉死「绝不带 `[pi]`」。
 * 按后缀判会把员工每一次正常选 ChatGPT/Grok 模型都打成 throw，而 renderer 的 `catch` 只
 * `console.error`：不落库、不提示、看起来像点了没反应。
 *
 * 网关组**刻意不校验**：成员判定要靠 catalog，而 catalog 可能陈旧（清单没同步 / 新模型刚上），
 * 在这里 fail-closed 的风险大于收益。网关组的同类误写由「意图必须显式」那一层挡。
 */
export function brainPickMismatchReason(pick) {
    const wallet = brainWalletForGroup(pick.group);
    const isClaudeFamily = isClaudeFamilyModel(pick.modelId);
    if (wallet === CLAUDE_OAUTH_GROUP) {
        // 裸别名 opus/sonnet/haiku 是 Claude OAuth 上**有意可执行**的路由（CLI 原生解析），
        // isClaudeFamilyModel 的 CLAUDE_BARE_ALIASES 分支放行它们。
        return isClaudeFamily
            ? undefined
            : `model ${pick.modelId} is not a Claude-family route for the Claude subscription group`;
    }
    if (isPersonalPiWallet(wallet)) {
        return isClaudeFamily
            ? `model ${pick.modelId} is a Claude-family route and cannot belong to personal subscription group ${pick.group}`
            : undefined;
    }
    return undefined;
}
/** 员工手选与平台默认物化共用的 app_settings 写形状。
 *
 * **这是「某张模型来源卡记住哪个模型」的唯一写点** —— main 的 `applyBrainPickToStore` 与
 * renderer 的 `buildMainModelSettings` 都经它，所以保险栏放在这里就覆盖了全部写入路径。 */
export function buildBrainPickSettings(pick, prevBrainGroupParams) {
    const mismatch = brainPickMismatchReason(pick);
    if (mismatch)
        throw new Error(`[brain-pick] refusing to store a mismatched pick: ${mismatch}`);
    const prevParams = prevBrainGroupParams?.[pick.group] ?? {};
    return {
        activeBrainGroup: pick.group,
        brainGroupParams: {
            ...(prevBrainGroupParams ?? {}),
            [pick.group]: { ...prevParams, defaultModel: pick.modelId, roleMapping: undefined },
        },
        defaultModel: pick.modelId,
    };
}
//# sourceMappingURL=brain-priority.js.map